import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { type CompoundEntityRef } from '@backstage/catalog-model';
import {
  createFrontendModule,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import {
  TechDocsAddonLocations,
  useShadowRoot,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { AddonBlueprint } from '@backstage/plugin-techdocs-react/alpha';
import {
  FloatingProposalButton,
  PageProposalButton,
  type SelectionSnapshot,
  useSelectionSnapshot,
} from './proposalSelection';

const SLUG_ANNOTATION = 'code.overheid.nl/project-slug';
const TECHDOCS_ANNOTATION = 'backstage.io/techdocs-ref';
const CONTENT_SELECTOR = '.md-content__inner';
const MAX_SELECTION = 5_000;

type Passage = { original: string; occurrence: number; line: number };

type Located = {
  path: string;
  sha: string;
  status: 'match' | 'several' | 'none';
  passages: Passage[];
};

type Step =
  | { name: 'loading' }
  | { name: 'choose'; located: Located }
  | { name: 'edit'; located: Located; passage: Passage; notFound: boolean }
  | { name: 'done'; url: string; path: string }
  | { name: 'failed'; message: string };

/** The page path after the root of the docs, which is where the source file hangs from. */
function currentPage({ namespace, kind, name }: CompoundEntityRef): string {
  const path = decodeURI(window.location.pathname);
  const prefix = [`/catalog/${namespace}/${kind}/${name}/docs`, `/docs/${namespace}/${kind}/${name}`].find(root =>
    path.toLowerCase().startsWith(root.toLowerCase()),
  );
  return prefix ? path.slice(prefix.length) : '';
}

function DocsProposal() {
  const { entityRef, entityMetadata } = useTechDocsReaderPage();
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const sheet = useRef<HTMLElement>(null);
  const edited = useRef<HTMLElement & { value: string }>(null);
  const reason = useRef<HTMLElement & { value: string }>(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>({ name: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string>();
  const shadowRoot = useShadowRoot();
  const selectionScope = useCallback(() => {
    const root = shadowRoot as (ShadowRoot & { getSelection?: () => Selection | null }) | undefined;
    return {
      selection: root?.getSelection ? root.getSelection() : document.getSelection(),
      content: root?.querySelector(CONTENT_SELECTOR) ?? null,
    };
  }, [shadowRoot]);
  const selection = useSelectionSnapshot(selectionScope, open);

  const annotations = entityMetadata.value?.metadata.annotations;
  const available = Boolean(annotations?.[SLUG_ANNOTATION] && annotations?.[TECHDOCS_ANNOTATION]);

  useEffect(() => {
    const element = sheet.current;
    const onClose = () => setOpen(false);
    element?.addEventListener('close', onClose);
    return () => element?.removeEventListener('close', onClose);
  }, [available]);

  const post = useCallback(
    async (route: string, body: unknown) => {
      const base = await discovery.getBaseUrl('forgejo');
      const { namespace, kind, name } = entityRef;
      const response = await fetch(`${base}/entity/${namespace}/${kind}/${name}/docs/${route}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => undefined);
      if (!response.ok) {
        throw new Error(result?.error?.message ?? 'Dit is niet gelukt.');
      }
      return result;
    },
    [discovery, entityRef, fetch],
  );

  const start = async (snapshot?: SelectionSnapshot) => {
    setOpen(true);
    setFailure(undefined);
    setStep({ name: 'loading' });
    const text = snapshot && snapshot.text.length <= MAX_SELECTION ? snapshot.text : '';
    try {
      const located: Located = await post('locate', {
        page: currentPage(entityRef),
        text,
        before: text ? snapshot?.before : '',
        after: text ? snapshot?.after : '',
      });
      if (located.status === 'several') {
        setStep({ name: 'choose', located });
      } else {
        setStep({
          name: 'edit',
          located,
          passage: located.passages[0],
          notFound: located.status === 'none' && Boolean(snapshot),
        });
      }
    } catch (error: any) {
      setStep({ name: 'failed', message: error.message });
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (step.name !== 'edit' || submitting) {
      return;
    }
    const replacement = edited.current?.value ?? '';
    if (replacement === step.passage.original) {
      setFailure('Je hebt de tekst nog niet veranderd.');
      return;
    }
    setFailure(undefined);
    setSubmitting(true);
    try {
      const { url } = await post('propose', {
        path: step.located.path,
        sha: step.located.sha,
        original: step.passage.original,
        occurrence: step.passage.occurrence,
        replacement,
        reason: reason.current?.value ?? '',
      });
      setStep({ name: 'done', url, path: step.located.path });
    } catch (error: any) {
      setFailure(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!available) {
    return null;
  }

  const title = 'path' in step ? `Wijziging in ${step.path}` : step.name === 'edit' || step.name === 'choose'
    ? `Wijziging in ${step.located.path}`
    : 'Stel een wijziging voor';

  return (
    <>
      <nldd-spacer size="32" />
      <PageProposalButton onClick={() => start()} />
      {createPortal(
        <>
          {selection && !open && <FloatingProposalButton selection={selection} onClick={() => start(selection)} />}
          <nldd-sheet ref={sheet} placement="right" width="560px" open={open ? '' : undefined}>
            <nldd-page>
              <nldd-top-title-bar
                slot="header"
                text={title}
                dismiss-text={step.name === 'done' ? 'Sluit' : 'Annuleer'}
                collapse-anchor="voorstel-titel"
              />
              <nldd-simple-section>
                <nldd-title id="voorstel-titel" slot="header" size="2" text={title} heading-level="1" />
                {step.name === 'loading' && (
                  <nldd-inline-dialog icon="file-markdown" text="De bron wordt opgehaald" />
                )}
                {step.name === 'failed' && (
                  <nldd-banner variant="critical" text="Dit is niet gelukt" supporting-text={step.message} />
                )}
                {step.name === 'done' && (
                  <>
                    <nldd-banner
                      variant="success"
                      text="Je voorstel staat klaar"
                      supporting-text="Het team dat over deze dienst gaat, beoordeelt je pull request. Tot die tijd verandert er niets in de documentatie."
                    />
                    <nldd-spacer size="16" />
                    <nldd-button
                      appearance="primary"
                      href={step.url}
                      target="_blank"
                      text="Bekijk het voorstel op code.overheid.nl"
                    />
                  </>
                )}
                {step.name === 'choose' && (
                  <>
                    <nldd-rich-text>
                      <p>Deze tekst staat vaker op de pagina. Kies welke je bedoelt.</p>
                    </nldd-rich-text>
                    <nldd-spacer size="16" />
                    <nldd-list appearance="box-base" accessible-label="Plekken waar deze tekst staat">
                      {step.located.passages.map(passage => (
                        <nldd-list-item
                          key={`${passage.line}`}
                          button
                          onClick={() =>
                            setStep({ name: 'edit', located: step.located, passage, notFound: false })
                          }
                        >
                          <nldd-text-cell
                            text={passage.original.slice(0, 120)}
                            supporting-text={`Regel ${passage.line}`}
                          />
                        </nldd-list-item>
                      ))}
                    </nldd-list>
                  </>
                )}
                {step.name === 'edit' && (
                  <nldd-form>
                    <form onSubmit={submit}>
                      {step.notFound && (
                        <>
                          <nldd-banner
                            variant="neutral"
                            text="Passage niet precies teruggevonden"
                            supporting-text="Daarom zie je hieronder het hele bestand. Zoek de tekst op en pas die aan."
                          />
                          <nldd-spacer size="16" />
                        </>
                      )}
                      {failure && (
                        <>
                          <nldd-banner variant="critical" text="Niet voorgesteld" supporting-text={failure} />
                          <nldd-spacer size="16" />
                        </>
                      )}
                      <nldd-form-field
                        label="Tekst in Markdown"
                        supporting-label={`Vanaf regel ${step.passage.line}`}
                      >
                        <nldd-multi-line-text-field
                          key={`${step.located.sha}-${step.passage.line}`}
                          ref={edited}
                          name="tekst"
                          rows={Math.min(16, step.passage.original.split('\n').length + 2)}
                          value={step.passage.original}
                        />
                      </nldd-form-field>
                      <nldd-form-field
                        label="Reden"
                        supporting-label="Dit helpt het team bij de beoordeling"
                        optional
                      >
                        <nldd-text-field ref={reason} name="reden" maxlength={1000} />
                      </nldd-form-field>
                      <nldd-form-actions>
                        <nldd-button-group>
                          <nldd-button
                            appearance="primary"
                            type="submit"
                            text="Stel voor"
                            loading={submitting ? '' : undefined}
                            loading-text="Voorstel wordt gemaakt"
                          />
                        </nldd-button-group>
                      </nldd-form-actions>
                    </form>
                  </nldd-form>
                )}
              </nldd-simple-section>
            </nldd-page>
          </nldd-sheet>
        </>,
        document.body,
      )}
    </>
  );
}

const proposalAddon = AddonBlueprint.make({
  name: 'voorstel',
  params: {
    name: 'Stel een wijziging voor',
    location: TechDocsAddonLocations.Content,
    component: DocsProposal,
  },
});

export const docsProposalModule = createFrontendModule({
  pluginId: 'techdocs',
  extensions: [proposalAddon],
});
